import React, { useCallback, useEffect, useRef, useState } from 'react'

import { type AppInsightsProviderProps, type PageTrackingConfig } from '../types/config'
import { type GroupContext, type UserContext } from '../types/context'

import { initializeAppInsights } from '../core/initialization'
import { useEventTracking } from '../hooks/useEventTracking'
import { usePageTracking } from '../hooks/usePageTracking'
import { useSessionTracking } from '../hooks/useSessionTracking'
import AppInsightsContext from './AppInsightsContext'

export const AppInsightsProvider: React.FC<AppInsightsProviderProps> = ({
  instrumentationKey,
  children,
  debug = false,
  enableBatching = false,
  batchSize = 10,
  batchInterval = 5000,
  disabled = false,
  sessionHeartbeatInterval = 30000
}) => {
  const [{ appInsights }] = useState(() => initializeAppInsights(instrumentationKey))

  const [isEnabled, setIsEnabled] = useState(!disabled)
  const [isDebugMode, setIsDebugMode] = useState(debug)
  const [userContext, setUserContext] = useState<UserContext>({})
  const [groupContext, setGroupContext] = useState<GroupContext>({})
  const [pageTrackingConfig, setPageTrackingConfig] = useState<PageTrackingConfig>({
    includeHash: false,
    enableAutoPageNameCapture: true
  })

  const { sessionId, startSession, endSession, pauseSession, resumeSession, getSessionId, isSessionActiveValue } = useSessionTracking(
    appInsights,
    isEnabled,
    isDebugMode,
    userContext,
    groupContext,
    sessionHeartbeatInterval
  )

  const { capture, identify, eventQueue } = useEventTracking(
    appInsights,
    isEnabled,
    isDebugMode,
    userContext,
    groupContext,
    enableBatching,
    batchSize,
    batchInterval,
    sessionId
  )

  // Use page tracking hook
  const { trackPageView } = usePageTracking(appInsights, isEnabled, isDebugMode, userContext, groupContext, pageTrackingConfig, sessionId)

  // Initialize session on mount
  useEffect(() => {
    if (isEnabled) startSession()
    else endSession('component_unmount')
  }, [endSession, isEnabled, isSessionActiveValue, startSession])

  // Handle unmount and page unload to flush events and end session
  useEffect(() => {
    const handleUnload = () => {
      // Flush any queued events
      if (enableBatching && eventQueue) {
        eventQueue.flush()
      }

      // End the session
      endSession('page_unload')
    }

    window.addEventListener('beforeunload', handleUnload)

    return () => {
      window.removeEventListener('beforeunload', handleUnload)
    }
  }, [enableBatching, eventQueue, endSession])

  // Group context management
  const currentGroupContext = useRef<GroupContext>({})
  const compareGroupContext = useCallback((groupContext: GroupContext) => {
    return JSON.stringify(groupContext) === JSON.stringify(currentGroupContext.current)
  }, [])

  const group = useCallback(
    (accountId: string, tenantId?: string, subscriptionId?: string) => {
      if (!isEnabled || compareGroupContext({ accountId, tenantId, subscriptionId })) return

      const groupProps = {
        accountId,
        tenantId,
        subscriptionId
      }

      currentGroupContext.current = groupProps
      setGroupContext(groupProps)

      if (isDebugMode) {
        console.log('🔍 App Insights: Group context set', groupProps)
      }
    },
    [isEnabled, isDebugMode, compareGroupContext]
  )

  const clearGroup = useCallback(() => {
    setGroupContext({})

    if (isDebugMode && isEnabled) {
      console.log('🔍 App Insights: Group context cleared')
    }
  }, [isEnabled, isDebugMode])

  // User identification wrapper that updates state
  const currentUserContext = useRef<UserContext>({})
  const compareUserContext = useCallback((userContext: UserContext) => {
    return JSON.stringify(userContext) === JSON.stringify(currentUserContext.current)
  }, [])
  const identifyUser = useCallback(
    (userId: string, email: string, additionalProps: Record<string, any> = {}) => {
      if (!isEnabled || compareUserContext({ userId, email })) return

      const userProps = identify(userId, email, additionalProps)
      if (userProps) {
        currentUserContext.current = { userId, email }
        setUserContext(userProps)
      }
    },
    [compareUserContext, identify, isEnabled]
  )

  // Control methods
  const enable = useCallback(() => {
    setIsEnabled(true)
    appInsights.config.disableTelemetry = false

    // Restart session if needed
    if (!isSessionActiveValue()) {
      startSession()
    }

    if (isDebugMode) {
      console.log('🔍 App Insights: Enabled')
    }
  }, [appInsights.config, isDebugMode, startSession, isSessionActiveValue])

  const disable = useCallback(() => {
    setIsEnabled(false)
    appInsights.config.disableTelemetry = true

    // End current session
    if (isSessionActiveValue()) {
      endSession('telemetry_disabled')
    }

    if (isDebugMode) {
      console.log('🔍 App Insights: Disabled')
    }
  }, [appInsights.config, isDebugMode, endSession, isSessionActiveValue])

  const setDebugMode = useCallback((enabled: boolean) => {
    setIsDebugMode(enabled)
    console.log(`🔍 App Insights: Debug mode ${enabled ? 'enabled' : 'disabled'}`)
  }, [])

  const configurePageTracking = useCallback(
    (config: PageTrackingConfig) => {
      setPageTrackingConfig((prev) => ({ ...prev, ...config }))

      if (isDebugMode && isEnabled) {
        console.log('🔍 App Insights: Page tracking configuration updated', config)
      }
    },
    [isEnabled, isDebugMode]
  )

  const getContext = useCallback(
    () => ({
      user: { ...userContext },
      group: { ...groupContext },
      session: {
        sessionId: getSessionId(),
        isActive: isSessionActiveValue(),
        startTime: sessionId.current
      },
      isEnabled,
      isDebugMode,
      pageTrackingConfig: { ...pageTrackingConfig }
    }),
    [userContext, groupContext, getSessionId, isSessionActiveValue, sessionId, isEnabled, isDebugMode, pageTrackingConfig]
  )

  // Create context value
  const contextValue = {
    identify: identifyUser,
    capture,
    group,
    clearGroup,
    enable,
    disable,
    isEnabled: () => isEnabled,
    setDebugMode,
    getContext,
    trackPageView,
    configurePageTracking,
    appInsights,
    userContext,
    groupContext,
    pageTrackingConfig,
    // Session tracking methods
    startSession,
    endSession,
    pauseSession,
    resumeSession,
    getSessionId,
    isSessionActive: isSessionActiveValue
  }

  return <AppInsightsContext.Provider value={contextValue}>{children}</AppInsightsContext.Provider>
}

export default AppInsightsProvider
